import type { ListPageConfig } from '@/lib/filters/types'
import type { InvoiceStatus } from '@/gql/graphql'
import { STATUS_META, invoiceTypeLabel, RAISEABLE_INVOICE_TYPES } from './invoice-utils'
import { DUE_FILTER_OPTIONS } from './cfo-cockpit-model'
import { generateValidateSearch } from '@/lib/filters/validate-search'

export const INVOICE_STATUS_OPTIONS = (Object.keys(STATUS_META) as InvoiceStatus[]).map((s) => ({
  value: s,
  label: STATUS_META[s].label,
}))

export const INVOICE_TYPE_OPTIONS = RAISEABLE_INVOICE_TYPES.map((t) => ({
  value: t,
  label: invoiceTypeLabel(t),
}))

export const INVOICE_RAISED_BY_OPTIONS = [
  { value: 'direct', label: 'The supplier' },
  { value: 'on-behalf', label: 'On their behalf' },
]

/** Rows-per-page choices on the CLIENT cockpit (the mockup's 5 / 10 / 25). */
export const CFO_PAGE_SIZE_OPTIONS = [5, 10, 25]
export const CFO_DEFAULT_PAGE_SIZE = 10

/**
 * The route's `validateSearch` config — the UNION of every persona's filter fields.
 *
 * The page builds a narrower config per persona (`raisedBy` only appears where on-behalf raising
 * happens), but `validateSearch` runs before a persona is known and must not strip a param it
 * has simply not been told about. Widening here costs nothing: a `raisedBy` param that reaches a
 * persona without that filter is read by no field and rendered by no control.
 */
export const INVOICES_ROUTE_VALIDATE_SEARCH_CONFIG: ListPageConfig = {
  routeId: '/invoices/',
  pageId: 'invoices',
  pagination: { pageSize: 20, pageSizeOptions: CFO_PAGE_SIZE_OPTIONS },
  fields: [
    { id: 'status', label: 'Status', type: 'select', options: INVOICE_STATUS_OPTIONS },
    { id: 'invoiceType', label: 'Type', type: 'select', options: INVOICE_TYPE_OPTIONS },
    { id: 'raisedBy', label: 'Raised by', type: 'select', options: INVOICE_RAISED_BY_OPTIONS },
    // The CLIENT cockpit's filters. Supplier, currency and entity options come from the data, so
    // they are declared without options here (any value is kept; the page ignores one it has no
    // row for).
    { id: 'q', label: 'Search', type: 'search' },
    { id: 'supplier', label: 'Supplier', type: 'select' },
    { id: 'currency', label: 'Currency', type: 'select' },
    { id: 'due', label: 'Due', type: 'select', options: DUE_FILTER_OPTIONS },
    { id: 'entity', label: 'Entity', type: 'select' },
    // An agency's client scope — a managed client's tenant id, or its own ("My organisation").
    { id: 'client', label: 'Client', type: 'select' },
  ],
}

const validateInvoiceListSearch = generateValidateSearch(INVOICES_ROUTE_VALIDATE_SEARCH_CONFIG)

/**
 * Keep the selected review drawer in the URL without pretending it is a list filter.
 * A direct `/invoices?invoice=<id>` visit can therefore restore the drawer while all existing
 * tab, scope, filter and pagination validation remains unchanged.
 */
export function validateInvoicesSearch(raw: Record<string, unknown>): Record<string, unknown> {
  return {
    ...validateInvoiceListSearch(raw),
    invoice: typeof raw.invoice === 'string' && raw.invoice ? raw.invoice : undefined,
  }
}
